/**
 * SocketContext — web port of the Socket.IO data channel that RN's
 * `apps/WunderGuide/hooks/useAudioChat.ts` opens to the voice gateway.
 *
 * Connects to VOICE_SERVER_URL (path `/api/v1/socket.io/`, websocket transport)
 * and re-emits the gateway's events:
 *   • partial / final / assistant      → transcript stream
 *   • audio / welcomeAck / playSFX     → base64 audio chunks
 *   • webData                          → carousel/data items (deals, places…)
 *   • balanceUpdate                    → push balance refreshes
 *   • showAccountDrawer / showPurchaseModal → server-driven UI directives
 *
 * Client → server emits:
 *   • hello {token, sessionId, language, …}
 *   • tokenUpdate, userUpdate, locationUpdate
 *
 * Subscribers register via `useSocketEvent('webData', cb)` etc. The hook keeps
 * its own listener so multiple consumers can listen to the same event.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import config from '../../config/env.json';
import { getAuthToken } from '../auth';
import logger from '../utils/logger';

type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error';

interface SocketContextValue {
  socket: Socket | null;
  connectionState: ConnectionState;
  /** Lazily open the connection (and authenticate). Idempotent. */
  connect: () => Promise<void>;
  disconnect: () => void;
  /** Convenience: emit if connected, no-op otherwise. */
  emit: <T = any>(event: string, payload?: T) => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const socketRef = useRef<Socket | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('idle');

  const connect = useCallback(async () => {
    if (socketRef.current?.connected) return;
    if (socketRef.current) {
      socketRef.current.connect();
      return;
    }

    const token = await getAuthToken();
    setConnectionState('connecting');
    const s = io(config.VOICE_SERVER_URL, {
      autoConnect: true,
      transports: ['websocket'],
      path: '/api/v1/socket.io/',
      auth: token ? { token } : undefined,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
    });

    s.on('connect', () => {
      logger.info('[Socket] connected', { id: s.id });
      setConnectionState('connected');
      s.emit('hello', {
        token,
        client: 'web',
        appId: 'wunderguide-web',
      });
    });
    s.on('disconnect', reason => {
      logger.info('[Socket] disconnected', { reason });
      setConnectionState('disconnected');
    });
    s.on('connect_error', err => {
      logger.warn('[Socket] connect error', { err: String(err) });
      setConnectionState('error');
    });

    socketRef.current = s;
  }, []);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
    setConnectionState('disconnected');
  }, []);

  const emit = useCallback(<T,>(event: string, payload?: T) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, payload);
    }
  }, []);

  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  const value = useMemo<SocketContextValue>(
    () => ({
      socket: socketRef.current,
      connectionState,
      connect,
      disconnect,
      emit,
    }),
    [connectionState, connect, disconnect, emit],
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export function useSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}

/**
 * Subscribe to a server event for the lifetime of the calling component.
 * Auto-unsubscribes on unmount and on socket replacement.
 */
export function useSocketEvent<T = any>(
  event: string,
  handler: (data: T) => void,
): void {
  const { socket } = useSocket();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!socket) return;
    const fn = (data: T) => handlerRef.current(data);
    socket.on(event, fn);
    return () => {
      socket.off(event, fn);
    };
  }, [socket, event]);
}
